import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, LogIn, Home as HomeIcon } from "lucide-react";
import { getSite } from "@/lib/site";
import { setMeta, setCanonical } from "@/lib/seo";

const VIDEO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_agent-command-96/artifacts/4i3bcz4n_no-address-60s%20Cinematic%20Master%20%281%29.mp4";

export default function Landing() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 12000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    getSite().then((s) => {
      if (s.home_title) document.title = s.home_title;
      if (s.home_description) setMeta("description", s.home_description);
      setMeta("og:title", s.home_title, true); setMeta("og:description", s.home_description, true);
      if (s.canonical_host) { setCanonical(`https://${s.canonical_host}/`); setMeta("og:url", `https://${s.canonical_host}/`, true); }
    });
  }, []);

  const onTime = (e) => {
    if (e.target.currentTime >= 12) { e.target.pause(); setRevealed(true); }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0A1533]" data-testid="landing-page">
      <video
        ref={videoRef}
        src={VIDEO_URL}
        autoPlay muted playsInline
        onTimeUpdate={onTime}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${revealed ? "opacity-30" : "opacity-100"}`}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1533]/40 via-[#0A1533]/30 to-[#0A1533]/85" />

      {/* Brand */}
      <div className="relative z-10 flex items-center gap-3 p-6 sm:p-10">
        <div className="w-11 h-11 rounded-sm bg-[#F7941D] flex items-center justify-center">
          <Building2 className="w-6 h-6 text-white" strokeWidth={1.5} />
        </div>
        <span className="font-serif text-2xl text-white">MattC Team</span>
      </div>

      {!revealed && (
        <button data-testid="landing-skip" onClick={() => setRevealed(true)}
          className="absolute z-20 bottom-8 right-8 text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white transition-colors">
          Skip intro →
        </button>
      )}

      {/* CTA panel */}
      <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center px-6 transition-all duration-1000 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"}`}>
        <div className="text-center max-w-xl">
          <div className="text-xs tracking-[0.35em] uppercase text-[#F7941D] font-semibold mb-4">MattC Team · Command Center</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
            Where every listing, deal and client lives.
          </h1>
          <p className="mt-5 text-[#C9D3EC] text-base sm:text-lg">
            Team members sign in to the command center. Just browsing? Explore our current listings.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button data-testid="landing-login-btn" onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#F7941D] text-white font-semibold hover:bg-[#E08312] transition-colors duration-200 w-full sm:w-auto justify-center">
              <LogIn className="w-5 h-5" /> Login
            </button>
          </div>
          <div className="mt-4">
            <button data-testid="landing-browse-btn" onClick={() => navigate("/browse")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors duration-200">
              <HomeIcon className="w-5 h-5" /> Browse Current Listings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
